import { LitElement, html } from 'lit';
import MicroModal from 'micromodal';
import { freeSampleCounts, addToCart } from '@Root/spree/api';
import { updateCartIndicator } from '@Root/spree/dom';
import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import '@Root/lib/lit-3form-block/components/cta-button';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';
import { styles, externalLoginFormOverrides } from './css';
import {
  analyticsCtaDataObject,
  handleAdobeNavEvent,
} from '../../helpers/adobe-analytics';
import { openContactRepModal } from '../../helpers/contact-rep-modal';
import threeformSession from '../../modules/threeform-session';
import { externalNgLoader, logErrorToSentry } from '../../helpers/helpers';

const EXTERNAL_LOGIN_FORM_TAG = 'external-login-form';
const EXTERNAL_LOGIN_FORM_TIMEOUT_MS = 10000;
const EXTERNAL_LOGIN_FORM_STYLE_ID =
  'add-to-cart-modal-external-login-form-styles';

const getExternalNgVersion = () =>
  document.querySelector('meta[name="external-ng-version"]')?.content || '';

const ensureExternalLoginFormStyles = () => {
  if (document.getElementById(EXTERNAL_LOGIN_FORM_STYLE_ID)) return;

  const styleElement = document.createElement('style');
  styleElement.id = EXTERNAL_LOGIN_FORM_STYLE_ID;
  styleElement.textContent = externalLoginFormOverrides;
  document.head.appendChild(styleElement);
};

class AddToCartModal extends LitElement {
  static properties = {
    loggedIn: {
      type: Boolean,
      state: true,
    },
    primaryCtaDisabled: { type: Boolean },
    _freeSampleCountResponse: { type: Boolean },
    title: { type: String },
    subtitle: { type: String },
    imageUrl: { type: String },
    productVariantId: { type: Number },
    analyticsProductId: { type: Number },
    _allowedFreeSamples: { type: String },
    _currentFreeSamples: { type: String },
    _displayPrice: { type: String },
    _isStudent: { type: Boolean },
    _showDisplayPrice: { type: Boolean },
    _loadingData: { type: Boolean },
    _addedToCart: { type: Boolean },
    _addingToCart: { type: Boolean },
  };

  constructor() {
    super();
    this.setDefaultDisplayValues();
    this._loadingExternalLoginForm = false;
    this._externalLoginForm = null;
    this.handleAuthenticatedUserUpdated = this.handleAuthenticatedUserUpdated.bind(
      this
    );
    this.handleExternalLogin = this.handleExternalLogin.bind(this);
    this.handleViewCartClick = this.handleViewCartClick.bind(this);

    window.addEventListener('setupAddToCartData', e => {
      const details = e.detail || null;
      if (details) {
        this.setupSampleData(details);
      }
    });
  }

  setDefaultDisplayValues() {
    this.loggedIn = this.loggedIn || threeformSession.hasAuthenticatedUser();
    this.primaryCtaDisabled = true;
    this._freeSampleCountResponse = false;
    this.title = '';
    this.subtitle = '';
    this.imageUrl = '';
    this.productVariantId = null;
    this.analyticsProductId = null;
    this._allowedFreeSamples = null;
    this._currentFreeSamples = null;
    this._displayPrice = null;
    this._isStudent = false;
    this._showDisplayPrice = false;
    this._addedToCart = false;
    this._addingToCart = false;
    this._activated = false;
  }

  subtitleHTML(details) {
    const sampleType = details.sampleType || '';
    const sampleTypeFormatted =
      sampleType[0].toUpperCase() + sampleType.substring(1, sampleType.length);
    const size =
      typeof details.productSize === 'string'
        ? details.productSize
        : details.productSize?.attributes.value;
    return html`
      ${size} ${sampleTypeFormatted} Sample
      ${!!details.gaugeStr
        ? html`
            <br />
            ${details.gaugeStr}
          `
        : html``}
    `;
  }

  async connectedCallback() {
    super.connectedCallback();
    ensureExternalLoginFormStyles();
    window.addEventListener(
      threeformSession.AUTHENTICATED_USER_UPDATED_EVENT,
      this.handleAuthenticatedUserUpdated
    );
    // The confirmation view's View Cart link is server-rendered beside this
    // element, so its clicks are picked up on the shared modal.
    this._modalElement = this.closest('.modal');
    this._modalElement?.addEventListener('click', this.handleViewCartClick);
  }

  updated() {
    // Defer the external login form (and the ngapps bundle it pulls in) until
    // the user has actually triggered an add-to-cart flow.
    if (!this.loggedIn && this._activated) {
      this.loadExternalLoginForm();
    }
  }

  disconnectedCallback() {
    window.removeEventListener(
      threeformSession.AUTHENTICATED_USER_UPDATED_EVENT,
      this.handleAuthenticatedUserUpdated
    );
    this._modalElement?.removeEventListener('click', this.handleViewCartClick);
    this._modalElement = null;

    if (this._externalLoginForm) {
      this._externalLoginForm.removeEventListener(
        'loggedIn',
        this.handleExternalLogin
      );
      this._externalLoginForm = null;
    }

    super.disconnectedCallback();
  }

  setupSampleData(details) {
    this._activated = true;
    this._loadingData = true;
    this.loggedIn = this.loggedIn || threeformSession.hasAuthenticatedUser();
    this.title = details.headerStr;
    this.subtitle = this.subtitleHTML(details);
    this.imageUrl = details.imageUrl;
    this.productVariantId = details.productVariantId;
    this.analyticsProductId = details.analyticsProductId;
    if (this.loggedIn) {
      this.setupSampleCountsData();
    }
  }

  setupSampleCountsData() {
    freeSampleCounts(this.productVariantId).then(response => {
      if (response) {
        this._freeSampleCountResponse = true;
        this._currentFreeSamples = response.current_free_samples;
        this._allowedFreeSamples = response.allowed_free_samples;
        this._displayPrice = response.display_price;
        this._isStudent = response.is_student;
        if (this._isStudent) this._allowedFreeSamples = 0;
        this._showDisplayPrice =
          this._allowedFreeSamples !== null &&
          Number(this._currentFreeSamples) >= Number(this._allowedFreeSamples);
        this.primaryCtaDisabled = false;
      }
    });
  }

  static get styles() {
    return [LIT_CSS_VARIABLES, SHARED_ELEMENTS, SHARED_CLASSES, styles];
  }

  render() {
    return this.loggedIn ? this.addToCartHTML : this.loginHTML;
  }

  handleAuthenticatedUserUpdated() {
    const isLoggedIn = threeformSession.hasAuthenticatedUser();

    if (this.loggedIn === isLoggedIn) return;

    this.loggedIn = isLoggedIn;

    if (
      this.loggedIn &&
      this.productVariantId &&
      !this._freeSampleCountResponse
    ) {
      this.setupSampleCountsData();
    }
  }

  async loadExternalLoginForm() {
    if (this.loggedIn || this._loadingExternalLoginForm) return;

    try {
      this._loadingExternalLoginForm = true;
      await this.updateComplete;

      if (
        this.querySelector(
          `${EXTERNAL_LOGIN_FORM_TAG}[slot="external-login-form"]`
        )
      ) {
        return;
      }

      await externalNgLoader();
      await this.waitForExternalLoginDefinition();

      const externalLoginForm = document.createElement(EXTERNAL_LOGIN_FORM_TAG);
      externalLoginForm.classList.add('add-to-cart-modal-external-login-form');
      externalLoginForm.slot = 'external-login-form';
      externalLoginForm.showCreateAccount = true;
      externalLoginForm.addEventListener('loggedIn', this.handleExternalLogin);
      this._externalLoginForm = externalLoginForm;
      this.appendChild(externalLoginForm);
    } catch (error) {
      logErrorToSentry(error);
    } finally {
      this._loadingExternalLoginForm = false;
    }
  }

  waitForExternalLoginDefinition() {
    if (window.customElements?.get(EXTERNAL_LOGIN_FORM_TAG)) {
      return Promise.resolve();
    }

    return Promise.race([
      window.customElements.whenDefined(EXTERNAL_LOGIN_FORM_TAG),
      new Promise((_, reject) => {
        window.setTimeout(() => {
          reject(new Error('Timed out waiting for external-login-form'));
        }, EXTERNAL_LOGIN_FORM_TIMEOUT_MS);
      }),
    ]);
  }

  async handleExternalLogin() {
    this.loggedIn = true;

    if (this._externalLoginForm) {
      this._externalLoginForm.removeEventListener(
        'loggedIn',
        this.handleExternalLogin
      );
      this._externalLoginForm.remove();
      this._externalLoginForm = null;
    }

    try {
      await threeformSession.fetchAuthenticatedUser();
    } catch (_error) {
      // The external login event is enough to continue rendering the cart UI.
    }

    if (this.productVariantId && !this._freeSampleCountResponse) {
      this.setupSampleCountsData();
    }
  }

  get addToCartHTML() {
    return html`
      <section class="add-to-cart">
        ${this.contentHeaderHTML}
        <div class="content-wrapper">
          <div class="image-wrapper">
            <img src="${this.imageUrl}" alt="Solution Image" />
          </div>
          <div class="details-wrapper">
            <div class="headers">
              <h2>${this.title}</h2>
              <h3 class="description">${this.subtitle}</h3>
            </div>
            ${this._showDisplayPrice
              ? html`
                  <h3 class="price">${this.priceString}</h3>
                `
              : html``}
            ${this._isStudent
              ? this.studentSamplesPrompt
              : this.sampleLimitsString}
          </div>
        </div>
        <div class="ctas-wrapper">
          ${this.ctaButtonsList.map(button => {
            if (button.ctaType === 'primary') {
              return html`
                <cta-button
                  @click="${() => this.addToCart()}"
                  cta-type="${button.ctaType}"
                  cta-data=${JSON.stringify(button.ctaData)}
                  .disabled="${this.primaryCtaDisabled || this._addingToCart}"
                ></cta-button>
              `;
            } else {
              return html`
                <cta-button
                  cta-type="${button.ctaType}"
                  cta-data=${JSON.stringify(button.ctaData)}
                  .ctaAnalyticsData=${analyticsCtaDataObject(
                    button.ctaData,
                    'Cart Modal'
                  )}
                ></cta-button>
              `;
            }
          })}
        </div>
      </section>
    `;
  }

  get contentHeaderHTML() {
    return html`
      <div class="content-wrapper-header">
        <p class="description added-to-cart">
          ${this._addedToCart ? 'Added to cart' : ''}
        </p>
        <span class="close-modal" @click="${() => this.closeModal()}">
          <img src="/static/icons/SVG/close.svg" />
        </span>
      </div>
    `;
  }

  get priceString() {
    return this._freeSampleCountResponse ? `${this._displayPrice} each` : ``;
  }

  get sampleLimitsString() {
    return this._freeSampleCountResponse && this._allowedFreeSamples !== null
      ? html`
          <p class="description sample-limit">
            ${`First ${this._allowedFreeSamples} samples complimentary. Additional Samples ${this._displayPrice} ea.`}
          </p>
        `
      : html``;
  }

  get studentSamplesPrompt() {
    return html`
      <p class="description sample-limit">
        Students receive a 70% discount on samples. You can also contact your
        local
        <a class="contact-rep" @click=${() => this.openContactRep()}
          >3form sales rep</a
        >
        for free samples that can be dropped off at your school
      </p>
    `;
  }

  openContactRep() {
    openContactRepModal();
  }

  closeModal() {
    this.setDefaultDisplayValues();
    MicroModal.close('modal-request-sample');
  }

  handleViewCartClick(event) {
    const link = event.target.closest('.js-view-cart');
    if (!link) return;

    // Modified clicks open a new tab natively, so only plain ones are held.
    const modified = event.metaKey || event.ctrlKey || event.shiftKey;
    handleAdobeNavEvent({
      navString: 'NAV|Cart Modal|View Cart',
      destinationUrl: modified ? '' : link.href,
      event,
    });
  }

  get ctaButtonsList() {
    const list = [];
    // TODO: if (isSampleOrder) {
    list.push({
      ctaType: 'primary',
      ctaData: {
        text: 'Add to Cart',
        fullWidth: true,
      },
    });
    list.push({
      ctaType: 'secondary',
      ctaData: {
        text: 'View Cart',
        url: `${window.SpreeAPI.host}/cart`,
        fullWidth: true,
      },
    });
    // }
    return list;
  }

  async addToCart() {
    this._addingToCart = true;
    this.cart = await addToCart({
      variantId: this.productVariantId,
      quantity: 1,
      analyticsProductId: this.analyticsProductId,
    });
    if (this.cart) {
      updateCartIndicator(this.cart.item_count);
      this.setupSampleCountsData();
      this._addingToCart = false;
      this._addedToCart = true;
    }
  }

  get loginHTML() {
    return html`
      <section class="login-content">
        ${this.contentHeaderHTML}
        <h2>Log in to Order Samples</h2>
        <div class="external-login-form-container">
          <slot
            name="external-login-form"
            data-external-ng-version="${getExternalNgVersion()}"
          ></slot>
        </div>
      </section>
    `;
  }
}

customElements.define('add-to-cart-modal', AddToCartModal);
